/**
 * One-time, reviewed repair for rip-card cost basis.
 *
 * Background: a now-fixed bug in reallocateRipBasis redistributed the FULL box
 * basis across only the still-held pulls on every reload, so once some pulls
 * were sold/traded the box cost got counted twice — inflating later COGS and
 * making profitable boxes read as losses. The basis booked onto those past
 * sales/trades is frozen on their records, so it needs a one-time correction.
 *
 * This module recomputes a conserving, value-weighted allocation of each rip's
 * Basis Removed across ALL of its pulls (held + disposed) and proposes the
 * corrected COGS (sales) / Basis (trades) plus recomputed Realized Gain. The
 * user reviews a before/after table; only on confirm are the writes applied.
 */
import { DB } from "../lib/db";
import { openModal, closeModal } from "../components/modal";
import { escapeHtml, toast } from "../lib/dom";
import { fmt$, fmtDate } from "../utils/format";
import { reallocateAllRipBases } from "../lib/ripBasis";
import { render } from "../state";

interface SaleChange {
  kind: "sale";
  txnId: string; itemIndex: number;
  ripId: string; lotId: string; cardName: string; date: string; qty: number;
  oldBasis: number; newBasis: number;        // line COGS ($)
  oldRealized: number; newRealized: number;  // Sale Price − COGS
}
interface TradeChange {
  kind: "trade";
  txnId: string; outIndex: number;
  ripId: string; lotId: string; cardName: string; date: string; qty: number;
  perUnit: number;                           // new per-unit basis
  oldBasis: number; newBasis: number;        // line basis = per-unit × qty
  oldRealized: number; newRealized: number;  // (FMV − Basis) × qty, pre-cash
}
type RepairChange = SaleChange | TradeChange;

let _pendingPlan: RepairChange[] = [];

function lotLabel(lotId: string): string {
  const c = DB.table("cards").find(r => r["Card Lot ID"] === lotId);
  return c ? `${c["Card Name"] || lotId}${c["Set"] ? " · " + c["Set"] : ""}` : lotId;
}

/** Build the proposed corrections across every rip. Pure — no writes. */
export function computeRipBasisRepairPlan(): RepairChange[] {
  const changes: RepairChange[] = [];

  for (const rip of DB.table("rips")) {
    const ripId = rip["Rip ID"];
    const basisRemoved = Number(rip["Basis Removed"]) || 0;
    if (!ripId || basisRemoved <= 0) continue;

    const pulls = DB.table("cards").filter(c => c["Source ID"] === ripId);
    if (!pulls.length) continue;

    interface SaleLine { saleId: string; itemIndex: number; qty: number; salePrice: number; cogs: number; date: string }
    interface TradeLine { tradeId: string; outIndex: number; qty: number; fmv: number; basis: number; date: string }
    interface PullInfo {
      lotId: string; currentQty: number; currentFMV: number;
      saleLines: SaleLine[]; tradeLines: TradeLine[];
      originalQty: number; signal: number;
    }

    const infos: PullInfo[] = pulls.map(c => {
      const lotId = c["Card Lot ID"];
      const currentQty = Number(c["Qty"]) || 0;
      const currentFMV = Number(c["Current FMV"]) || 0;

      const saleLines: SaleLine[] = [];
      for (const sale of DB.table("sales")) {
        if (String(sale["Status"] || "Completed") !== "Completed") continue;
        const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
        items.forEach((it, idx) => {
          if (it["Lot ID"] === lotId) saleLines.push({
            saleId: sale["Sale ID"], itemIndex: idx,
            qty: Number(it["Qty"]) || 1,
            salePrice: Number(it["Sale Price"]) || 0,
            cogs: Number(it["COGS"]) || 0,
            date: sale["Date"] || "",
          });
        });
      }

      const tradeLines: TradeLine[] = [];
      for (const t of DB.table("trades")) {
        if (String(t["Status"] || "Accepted") !== "Accepted") continue;
        const out: any[] = Array.isArray(t.Outgoing) ? t.Outgoing : [];
        out.forEach((o, idx) => {
          if (o["Lot ID"] === lotId) tradeLines.push({
            tradeId: t["Trade ID"], outIndex: idx,
            qty: Number(o["Qty"]) || 1,
            fmv: Number(o["FMV"]) || 0,
            basis: Number(o["Basis"]) || 0,
            date: t["Date"] || "",
          });
        });
      }

      const disposedQty = saleLines.reduce((a, s) => a + s.qty, 0)
        + tradeLines.reduce((a, tl) => a + tl.qty, 0);
      const originalQty = Math.max(1, currentQty + disposedQty);
      const signal = (currentQty > 0 ? currentFMV : 0)
        + saleLines.reduce((a, s) => a + s.salePrice, 0)
        + tradeLines.reduce((a, tl) => a + tl.fmv * tl.qty, 0);

      return { lotId, currentQty, currentFMV, saleLines, tradeLines, originalQty, signal };
    });

    const totalSignal = infos.reduce((a, p) => a + Math.max(0, p.signal), 0);
    const numPulls = infos.length;

    for (const info of infos) {
      const pullAllocated = totalSignal > 0
        ? (Math.max(0, info.signal) / totalSignal) * basisRemoved
        : basisRemoved / numPulls;
      const perUnit = pullAllocated / info.originalQty;

      for (const sl of info.saleLines) {
        const newCOGS = +(perUnit * sl.qty).toFixed(2);
        if (Math.abs(newCOGS - sl.cogs) > 0.005) {
          changes.push({
            kind: "sale", txnId: sl.saleId, itemIndex: sl.itemIndex,
            ripId, lotId: info.lotId, cardName: lotLabel(info.lotId), date: sl.date, qty: sl.qty,
            oldBasis: +sl.cogs.toFixed(2), newBasis: newCOGS,
            oldRealized: +(sl.salePrice - sl.cogs).toFixed(2),
            newRealized: +(sl.salePrice - newCOGS).toFixed(2),
          });
        }
      }
      for (const tl of info.tradeLines) {
        const newPerUnit = +perUnit.toFixed(2);
        if (Math.abs(newPerUnit - tl.basis) > 0.005) {
          changes.push({
            kind: "trade", txnId: tl.tradeId, outIndex: tl.outIndex,
            ripId, lotId: info.lotId, cardName: lotLabel(info.lotId), date: tl.date, qty: tl.qty,
            perUnit: newPerUnit,
            oldBasis: +(tl.basis * tl.qty).toFixed(2), newBasis: +(newPerUnit * tl.qty).toFixed(2),
            oldRealized: +((tl.fmv - tl.basis) * tl.qty).toFixed(2),
            newRealized: +((tl.fmv - newPerUnit) * tl.qty).toFixed(2),
          });
        }
      }
    }
  }
  return changes;
}

/** Recompute a trade's per-item + total realized gain (mirrors saveTrade,
 *  including FMV-weighted spread of cash received). Mutates outgoing rows. */
function recomputeTradeRealized(trade: any): number {
  const outgoing: any[] = Array.isArray(trade.Outgoing) ? trade.Outgoing : [];
  const cash = Number(trade["Cash"]) || 0;       // + received / − paid
  const cashReceived = cash > 0 ? cash : 0;
  let total = 0;
  for (const out of outgoing) {
    const qtyOut = Number(out.Qty) || 1;
    const realized = +(((Number(out.FMV) || 0) - (Number(out.Basis) || 0)) * qtyOut).toFixed(2);
    out["Realized Gain"] = realized;
    total += realized;
  }
  total = +total.toFixed(2);
  if (cashReceived > 0 && outgoing.length) {
    const totOutFMV = outgoing.reduce((a, x) => a + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
    for (const out of outgoing) {
      const lineFMV = (Number(out.FMV) || 0) * (Number(out.Qty) || 1);
      const share = totOutFMV > 0 ? (lineFMV / totOutFMV) * cashReceived : cashReceived / outgoing.length;
      out["Realized Gain"] = +(((Number(out["Realized Gain"]) || 0) + share)).toFixed(2);
    }
    total = +outgoing.reduce((a, x) => a + (Number(x["Realized Gain"]) || 0), 0).toFixed(2);
  } else if (cashReceived > 0) {
    total = +(total + cashReceived).toFixed(2);
  }
  return total;
}

/** Apply a reviewed plan: rewrite sale/trade cost basis + realized gain, then
 *  reallocate held-card basis. Returns counts of records touched. */
export function applyRipBasisRepairPlan(plan: RepairChange[]): { sales: number; trades: number } {
  const saleGroups: Record<string, SaleChange[]> = {};
  const tradeGroups: Record<string, TradeChange[]> = {};
  for (const ch of plan) {
    if (ch.kind === "sale") { if (!saleGroups[ch.txnId]) saleGroups[ch.txnId] = []; saleGroups[ch.txnId].push(ch); }
    else { if (!tradeGroups[ch.txnId]) tradeGroups[ch.txnId] = []; tradeGroups[ch.txnId].push(ch); }
  }

  let salesTouched = 0;
  for (const saleId of Object.keys(saleGroups)) {
    const sale = DB.table("sales").find(s => s["Sale ID"] === saleId);
    if (!sale) continue;
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
    for (const ch of saleGroups[saleId]) {
      let it = items[ch.itemIndex];
      if (!it || it["Lot ID"] !== ch.lotId) it = items.find(x => x["Lot ID"] === ch.lotId);
      if (!it) continue;
      it["COGS"] = ch.newBasis;
      it["Realized Gain"] = +(((Number(it["Sale Price"]) || 0) - ch.newBasis)).toFixed(2);
    }
    const subtotal = items.reduce((a, x) => a + (Number(x["Sale Price"]) || 0), 0);
    const totalCOGS = items.reduce((a, x) => a + (Number(x["COGS"]) || 0), 0);
    const fees = Number(sale["Fees"]) || 0, ship = Number(sale["Shipping"]) || 0;
    const net = +(subtotal - fees - ship - totalCOGS).toFixed(2);
    DB.update("sales", "Sale ID", saleId, { "Items": items, "COGS": +totalCOGS.toFixed(2), "Net Profit": net });
    salesTouched++;
  }

  let tradesTouched = 0;
  for (const tradeId of Object.keys(tradeGroups)) {
    const trade = DB.table("trades").find(t => t["Trade ID"] === tradeId);
    if (!trade) continue;
    if (String(trade["Status"] || "Accepted") !== "Accepted") continue;
    const out: any[] = Array.isArray(trade.Outgoing) ? trade.Outgoing : [];
    for (const ch of tradeGroups[tradeId]) {
      let o = out[ch.outIndex];
      if (!o || o["Lot ID"] !== ch.lotId) o = out.find(x => x["Lot ID"] === ch.lotId);
      if (!o) continue;
      o["Basis"] = ch.perUnit;
    }
    const totalRealized = recomputeTradeRealized(trade);
    DB.update("trades", "Trade ID", tradeId, { "Outgoing": out, "Realized Gain": totalRealized });
    tradesTouched++;
  }

  // Held cards now absorb the not-yet-realized remainder of each box's basis.
  reallocateAllRipBases();
  return { sales: salesTouched, trades: tradesTouched };
}

/* ---------------- UI: review modal ---------------- */

export function openRipBasisRepair(): void {
  const plan = computeRipBasisRepairPlan();
  _pendingPlan = plan;
  if (!plan.length) {
    toast("No rip cost-basis corrections needed — everything reconciles.");
    return;
  }
  const totalOld = +plan.reduce((a, c) => a + c.oldRealized, 0).toFixed(2);
  const totalNew = +plan.reduce((a, c) => a + c.newRealized, 0).toFixed(2);
  const delta = +(totalNew - totalOld).toFixed(2);

  const rows = plan.map(c => `<tr>
    <td>${escapeHtml(fmtDate(c.date))}</td>
    <td>${escapeHtml(c.cardName)}</td>
    <td class="font-mono text-xs">${escapeHtml(c.txnId)}</td>
    <td>${c.kind === "sale" ? "Sale" : "Trade"}</td>
    <td class="text-right stat-num text-[var(--muted)]">${fmt$(c.oldBasis)}</td>
    <td class="text-right stat-num">${fmt$(c.newBasis)}</td>
    <td class="text-right stat-num text-[var(--muted)]">${fmt$(c.oldRealized)}</td>
    <td class="text-right stat-num ${c.newRealized >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${fmt$(c.newRealized)}</td>
  </tr>`).join("");

  const body = `
    <div class="text-sm text-[var(--muted)] mb-3">
      A fixed allocation bug previously double-counted box cost across pulled cards, distorting the cost basis booked on these <b>${plan.length}</b> disposition${plan.length === 1 ? "" : "s"}.
      The box cost is re-spread across each rip's pulls by value — held cards at current FMV, sold cards at sale price, traded cards at trade FMV — so booked basis + held basis equals each box's cost exactly.
      <b>Nothing is written until you click Apply corrections.</b>
    </div>
    <div class="panel scrollx" style="max-height:52vh; overflow:auto">
      <table class="t"><thead><tr>
        <th>Date</th><th>Card</th><th>Txn</th><th>Type</th>
        <th class="text-right">Basis old</th><th class="text-right">Basis new</th>
        <th class="text-right">Realized old</th><th class="text-right">Realized new</th>
      </tr></thead><tbody>${rows}</tbody></table>
    </div>
    <div class="mt-3 text-sm">Net change to total realized gain across these lines:
      <b class="${delta >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${delta >= 0 ? '+' : ''}${fmt$(delta)}</b>
      <span class="text-[var(--muted)]">(was ${fmt$(totalOld)} → now ${fmt$(totalNew)})</span>.</div>`;

  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="applyRipBasisRepair()">Apply corrections</button>`;
  openModal({ title: "Repair rip cost basis", body, footer, wide: true });
}

export function applyRipBasisRepair(): void {
  const plan = _pendingPlan;
  if (!plan.length) { closeModal(); return; }
  const res = applyRipBasisRepairPlan(plan);
  _pendingPlan = [];
  try { localStorage.setItem("enigma_ripbasis_repair_v1", "done"); } catch { /* ignore */ }
  closeModal();
  toast(`Cost basis repaired across ${res.sales} sale(s) and ${res.trades} trade(s).`);
  render();
}

/** Number of disposition lines that would change — used by the load prompt. */
export function ripBasisRepairCount(): number {
  return computeRipBasisRepairPlan().length;
}
export function ripBasisRepairPromptedV1(): boolean {
  try { return localStorage.getItem("enigma_ripbasis_repair_v1") != null; } catch { return false; }
}
export function markRipBasisRepairPrompted(): void {
  try { localStorage.setItem("enigma_ripbasis_repair_v1", "seen"); } catch { /* ignore */ }
}

// Inline-handler bridges
(window as any).openRipBasisRepair = openRipBasisRepair;
(window as any).applyRipBasisRepair = applyRipBasisRepair;
